import { useState, useCallback, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { validateEmail, validatePassword, passwordsMatch } from '../utils/validation.utils';
import { USERS_API_BASE_URL } from '../constants';

export type PasswordResetStep = 'email' | 'otp' | 'password';

interface ResetPasswordEmailRequest {
  email: string;
}

interface VerifyOtpRequest {
  verificationCode: string;
  uuid: string;
}

interface CreateNewPasswordRequest {
  email: string;
  verificationCode: string;
  uuid: string;
  password: string;
  confirmPassword: string;
}

/**
 * Custom hook for handling password reset flow
 * Manages the multi-step password reset process
 */
export const usePasswordReset = () => {
  const [step, setStep] = useState<PasswordResetStep>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uuid, setUuid] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [otp, setOtp] = useState<string>('');
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Step 1: Request password reset email
   */
  const requestPasswordReset = useCallback(async (data: ResetPasswordEmailRequest) => {
    try {
      if (isMountedRef.current) {
        setIsLoading(true);
        setError(null);
      }

      if (!data.email) {
        throw new Error('Please enter email address');
      }

      if (!validateEmail(data.email)) {
        throw new Error('Please enter a valid email address');
      }

      const response = await fetch(`${USERS_API_BASE_URL}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: data.email }),
      });

      const result = await response.json();

      if (result.status === 400) {
        throw new Error(result.msg || 'Failed to send reset email');
      }

      if (isMountedRef.current) {
        setEmail(data.email);
        setUuid(result.userId);
        setStep('otp');
      }
      toast.success('Password reset code has been sent to your email');

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send reset email';
      if (isMountedRef.current) {
        setError(message);
      }
      toast.error(message);
      throw err;
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  /**
   * Step 2: Verify OTP
   */
  const verifyOtp = useCallback(async (data: VerifyOtpRequest) => {
    try {
      if (isMountedRef.current) {
        setIsLoading(true);
        setError(null);
      }

      if (!data.verificationCode) {
        throw new Error('Please enter the verification code');
      }

      const response = await fetch(`${USERS_API_BASE_URL}/reset-password/verify-access-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          verificationCode: data.verificationCode,
          uuid: data.uuid
        }),
      });

      const result = await response.json();

      if (result.status === 400) {
        throw new Error(result.error || 'Invalid verification code');
      }

      if (isMountedRef.current) {
        setUuid(result.userDetail.uuid);
        setOtp(data.verificationCode);
        setStep('password');
      }
      toast.success('Verification successful. Please create a new password.');

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Verification failed';
      if (isMountedRef.current) {
        setError(message);
      }
      toast.error(message);
      throw err;
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  /**
   * Reset the password reset state
   */
  const resetPasswordResetState = useCallback(() => {
    setStep('email');
    setError(null);
    setUuid('');
    setEmail('');
    setOtp('');
  }, []);

  /**
   * Step 3: Create new password
   */
  const createNewPassword = useCallback(async (data: CreateNewPasswordRequest) => {
    try {
      if (isMountedRef.current) {
        setIsLoading(true);
        setError(null);
      }

      if (!data.password) {
        throw new Error('Please enter password');
      }

      if (!data.confirmPassword) {
        throw new Error('Please enter confirm password');
      }

      if (!passwordsMatch(data.password, data.confirmPassword)) {
        throw new Error('Passwords do not match');
      }

      const passwordValidation = validatePassword(data.password);
      if (!passwordValidation.valid) {
        throw new Error(passwordValidation.errors[0]);
      }

      const response = await fetch(`${USERS_API_BASE_URL}/reset-password-create-new-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: data.email,
          verificationCode: data.verificationCode,
          uuid: data.uuid,
          password: data.password,
          confirmPassword: data.confirmPassword
        }),
      });

      const result = await response.json();

      if (result.status === 400) {
        throw new Error(result.msg || 'Failed to reset password');
      }

      toast.success('Password reset successfully. Please login with your new password.');
      
      // Reset state inline
      if (isMountedRef.current) {
        setStep('email');
        setError(null);
        setUuid('');
        setEmail('');
        setOtp('');
      }

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset password';
      if (isMountedRef.current) {
        setError(message);
      }
      toast.error(message);
      throw err;
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  /**
   * Cancel password reset and return to login
   */
  const cancelPasswordReset = useCallback(() => {
    setStep('email');
    setError(null);
    setUuid('');
    setEmail('');
    setOtp('');
  }, []);

  return {
    step,
    isLoading,
    error,
    uuid,
    email,
    otp,
    setError,
    requestPasswordReset,
    verifyOtp,
    createNewPassword,
    cancelPasswordReset,
    resetPasswordResetState
  };
};
